import type { CampaignSource, CampaignsResponse, CampaignTypes, IPrizePopCampaign } from '../../types/campaign.types';
import { TOURNAMENTS_CAMPAIGN_TYPE, REBATE_CAMPAIGN_TYPE } from '../../types/campaign.types';
import type { ITournament, IParsedTournament } from '../../types/variants/tournament.types';
import { isCampaign } from '../../utils/campaignHelpers';
import { isTournamentSource, parseTournamentsData, sortTournaments, updateTournamentGameFlags } from '../../utils/variants/tournamentHelpers';
import { sortPrizePopCampaigns } from '../../utils/variants/prizePopHelpers';
import { sortRebateCampaigns } from '../../utils/variants/rebateHelpers';
import { campaignConfigRegistry } from '../../configs';
import { useAppStore } from '../../../../store/app/appStore';

type CampaignSorter = (campaigns: CampaignSource[]) => CampaignSource[];

const campaignSorters: Partial<Record<CampaignTypes, CampaignSorter>> = {
  // [PRIZE_POP_CAMPAIGN_TYPE]: uses sortPrizePopCampaigns by default, no need to specify here
  [REBATE_CAMPAIGN_TYPE]: sortRebateCampaigns as CampaignSorter,
};

interface PanelInfoFields {
  prizeFund: string;
  prizeCurrency: string;
  headerTitle: string;
}

export const computePanelInfoFields = (
  campaignsForType: CampaignSource[],
  resolvedType: CampaignTypes,
): PanelInfoFields => {
  const isSingleCampaign = campaignsForType.length === 1;
  const config = campaignConfigRegistry[resolvedType];
  const sectionContext = useAppStore.getState().sections.find(s => s.name === config.sectionName)?.context;

  return {
    prizeFund: isSingleCampaign
      ? String(campaignsForType[0]?.prizeFund || '')
      : String(sectionContext?.prizeFund || ''),
    prizeCurrency: isSingleCampaign
      ? (campaignsForType[0]?.baseCurrency || '')
      : (sectionContext?.prizeCurrency || campaignsForType[0]?.baseCurrency || ''),
    headerTitle: isSingleCampaign
      ? (campaignsForType[0]?.name || config.displayName)
      : (sectionContext?.headerTitle || config.displayName),
  };
};

interface ProcessTournamentsResult {
  mergedCampaigns: CampaignSource[];
  newSelectedId: string | null;
}

interface ProcessCampaignResult {
  mergedCampaigns: CampaignSource[];
  newSelectedId: string | null;
}

/**
 * Processes tournament campaigns with parsing, sorting, and game flags
 * Merges with existing non-tournament campaigns (e.g., Prize Pop)
 */
export const processTournamentCampaigns = (
  response: CampaignsResponse,
  existingCampaigns: CampaignSource[],
  currentSelectedId: string | null
): ProcessTournamentsResult => {
  const tournamentItems = (response.campaigns as (ITournament | IParsedTournament)[])
    .map(t => ({ ...t, type: TOURNAMENTS_CAMPAIGN_TYPE })) as (ITournament | IParsedTournament)[];

  const parsed = parseTournamentsData(tournamentItems);
  updateTournamentGameFlags(parsed);

  // Keep non-tournament campaigns (e.g., Prize Pop)
  const existingOtherTypes = existingCampaigns.filter(c => !isTournamentSource(c));
  const existingTournaments = existingCampaigns.filter(isTournamentSource) as (ITournament | IParsedTournament)[];

  let processedTournaments: (ITournament | IParsedTournament)[];
  if (existingTournaments.length === 0) {
    // Initial fetch — apply full sort
    processedTournaments = sortTournaments(parsed);
  } else {
    // Subsequent fetch — preserve existing UI order so joining a tournament
    // (or any other state change) doesn't make the card jump in the list.
    const newById = new Map(parsed.map(t => [t.id, t]));
    const existingIds = new Set(existingTournaments.map(t => t.id));

    const preserved: (ITournament | IParsedTournament)[] = [];
    for (const t of existingTournaments) {
      const updated = newById.get(t.id);
      // Keep existing UI order, take fresh data as-is. Player Position/Score are no
      // longer kept on the campaign — the card reads them straight from the
      // leaderboard sub-fetch (see useCampaignInfoItems), so nothing to preserve.
      if (updated) preserved.push(updated);
    }

    const brandNew = parsed.filter(t => !existingIds.has(t.id));
    processedTournaments = [...preserved, ...sortTournaments(brandNew)];
  }

  const mergedCampaigns = [...existingOtherTypes, ...processedTournaments];

  const newSelectedId = !currentSelectedId && processedTournaments.length > 0
    ? processedTournaments[0].id
    : processedTournaments.length === 0 ? null : currentSelectedId;

  return { mergedCampaigns, newSelectedId };
};

/**
 * Processes campaigns (Prize Pop, Rebate) with UI order preservation for iOS stability.
 * The sorter is selected by campaign type — rebate uses sortRebateCampaigns,
 * everything else falls back to sortPrizePopCampaigns.
 */
export const processCampaigns = (
  response: CampaignsResponse,
  resolvedType: CampaignTypes,
  existingCampaigns: CampaignSource[],
  currentSelectedId: string | null
): ProcessCampaignResult => {
  const newCampaigns = (response.campaigns || []).map(campaign => ({
    ...campaign,
    type: resolvedType
  })) as CampaignSource[];
  
  const campaignType = resolvedType;
  const sortCampaigns = campaignSorters[campaignType] ?? (sortPrizePopCampaigns as CampaignSorter);

  // Keep tournaments and other campaign types untouched
  const existingOtherTypes = existingCampaigns.filter(c =>
    isTournamentSource(c) || (!campaignType || (isCampaign(c) && c.type !== campaignType))
  );
  const typedCampaigns = newCampaigns.filter(isCampaign);

  // Check if we already have campaigns of this type in the store
  const existingSameType = campaignType
    ? existingCampaigns.filter((c): c is IPrizePopCampaign => isCampaign(c) && c.type === campaignType)
    : [];

  let orderedCampaigns: CampaignSource[];

  if (existingSameType.length === 0) {
    // Initial fetch — apply full sort
    orderedCampaigns = sortCampaigns(typedCampaigns);
  } else {
    // Subsequent fetch — preserve existing UI order, update data in place.
    // This prevents cards from jumping around after user interactions
    // (e.g. joining a campaign), which causes rendering issues on iOS.
    const newById = new Map(typedCampaigns.map(c => [c.id, c]));
    const existingIds = new Set(existingSameType.map(c => c.id));

    // Keep existing campaigns in their current order, with fresh data
    const preserved: CampaignSource[] = [];
    for (const c of existingSameType) {
      const updated = newById.get(c.id);
      if (updated) preserved.push(updated);
    }

    // Any truly new campaigns get sorted and appended
    const brandNew = typedCampaigns.filter(c => !existingIds.has(c.id));
    orderedCampaigns = [...preserved, ...sortCampaigns(brandNew)];
  }

  const mergedCampaigns = [...existingOtherTypes, ...orderedCampaigns];
  const newSelectedId = currentSelectedId ?? (orderedCampaigns.length > 0 ? orderedCampaigns[0].id : null);

  return { mergedCampaigns, newSelectedId };
};
